import { CollectionPreferences, Spinner, TableProps } from "@amzn/awsui-components-react";
import { Pagination, Table } from "@amzn/uxm-aws-ui-components-react/lib/src/polaris";
import { buildColumnDefinitions, buildVisibleContentOptions } from "common/utils/table-utils";
import tableSchema from "./orders.table-config.json";
import { ColumnSetting } from "common/utils/config-models";
import useGetCustomQueryOrders, { DEFAULT_PAGE_SIZE } from "hooks/useGetCustomQueryOrders";
import { useCallback, useEffect } from "react";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { usePreferences } from "hooks/usePreferences";
import EmptyTableBox from "../EmptyTableBox/EmptyTableBox";
import { GetCatalogErrorContent } from "common/constants";
import { ErrorProps } from "interfaces/errorProps";
import { NonCancelableEventHandler } from "@amzn/awsui-components-react/polaris/internal/events";
import { Order } from "@amzn/ito-client";
import { SortingState } from "views/LocalITOrderList";
import { LOCAL_IT_ORDER_LIST } from "helpers";

const getStringPathError: string = "errors";

const columns: ColumnSetting[] = tableSchema.columns;
const PREFERENCES_KEY = "ITSEOrderList";
const VISIBLE_CONTENT_COLUMN_OPTIONS = buildVisibleContentOptions(columns);
const FIRST_PAGE_INDEX = 1;

/**
 * Props interface for the OrderItemsTable component
 *
 * @interface
 * @property {(error?: ErrorProps) => string | undefined} generateError - Function to generate error messages based on error props
 * @property {Record<string, any>} [query] - Optional query object to apply to the orders data
 * @property {boolean} [isLoading] - Optional loading state indicator
 * @property {SortingState} [sorting] - Optional sorting configuration for the table
 * @property {NonCancelableEventHandler<TableProps.SortingState<Order>>} [onSortingChange] - Optional callback handler for sorting changes
 */
interface OrderItemsTableProps {
    generateError: (error?: ErrorProps) => string | undefined;
    query?: Record<string, any>;
    isLoading?: boolean;
    sorting?: SortingState;
    onSortingChange?: NonCancelableEventHandler<TableProps.SortingState<Order>>;
}

/**
 * A table component for displaying order items with pagination, sorting, and filtering capabilities
 *
 * @param {OrderItemsTableProps} props - The props for the OrderItemsTable component
 * @return {JSX.Element} - A rendered table component with orders data, pagination, and preferences
 */
const OrderItemsTable = (props: OrderItemsTableProps) => {
    /* Table Preferences */
    const { currentPreferences, onPreferencesConfirmHandler } = usePreferences({
        preferencesKey: PREFERENCES_KEY,
        refreshData: () => refreshData(true),
        columnsSettings: columns,
        defaultPaging: DEFAULT_PAGE_SIZE,
    });

    /* ITO Orders */
    const orders = useGetCustomQueryOrders({
        query: props.query,
        isFiltersLoading: props.isLoading,
        pageSize: currentPreferences.pageSize,
    });

    const refreshData = useCallback(
        (clearPreviousData: boolean) => {
            if (clearPreviousData) {
                orders.pagination.onChange(FIRST_PAGE_INDEX);
            }
        },
        [orders.pagination],
    );

    /* Update page size when updated in the preferences */
    useEffect(() => {
        if (currentPreferences.pageSize === undefined) {
            return;
        }

        orders.pagination.onPageSizeChange(currentPreferences.pageSize);
    }, [currentPreferences.pageSize]);

    /* Handle error obtaining ITO orders */
    useEffect(() => {
        if (!orders.data.error) return;

        props.generateError({
            name: getString(`${getStringPathError}.unexpected`),
            content: GetCatalogErrorContent("inverted"),
        });
    }, [orders.data.error]);

    /* Loading Indicators */
    const isTableLoading = orders.data.isLoading || props.isLoading;
    const showPaginationSpinner = orders.data.isPageLoading;

    /* Table columns */
    const currentColumnDefinitions = buildColumnDefinitions(columns, currentPreferences.visibleContent);

    return (
        <Table
            data-testid={LOCAL_IT_ORDER_LIST.ORDERS_TABLE}
            touchpoint="order-items-table"
            pagination={
                showPaginationSpinner ? (
                    <Spinner />
                ) : (
                    <Pagination
                        data-testid={LOCAL_IT_ORDER_LIST.PAGINATION}
                        currentPageIndex={orders.pagination.currentPage}
                        pagesCount={orders.pagination.totalPages}
                        onChange={({ detail }) => orders.pagination.onChange(detail.currentPageIndex)}
                        ariaLabels={{
                            nextPageLabel: getString("common.pagination.nextPage"),
                            previousPageLabel: getString("common.pagination.previousPage"),
                            pageLabel: (pageNumber: number) =>
                                getString("common.pagination.currentPage", undefined, {
                                    pageNumber,
                                    pagesCount: orders.pagination.totalPages,
                                }),
                        }}
                        touchpoint="intake-order-items-pagination"
                    />
                )
            }
            preferences={
                <CollectionPreferences
                    data-testid={LOCAL_IT_ORDER_LIST.PREFERENCES}
                    title={getString("common.preferences.title")}
                    confirmLabel={getString("common.preferences.confirmLabel")}
                    cancelLabel={getString("common.preferences.cancelLabel")}
                    pageSizePreference={{
                        title: getString("common.preferences.pageSize.title"),
                        options: orders.pagination.getPageSizeOptions(),
                    }}
                    stripedRowsPreference={{
                        label: getString("common.preferences.stripedRows.label"),
                        description: getString("common.preferences.stripedRows.description"),
                    }}
                    visibleContentPreference={{
                        title: getString("common.preferences.visibleContentPreference.title"),
                        options: [
                            {
                                label: getString("common.preferences.visibleContentPreference.columns"),
                                options: VISIBLE_CONTENT_COLUMN_OPTIONS,
                            },
                        ],
                    }}
                    preferences={currentPreferences}
                    onConfirm={onPreferencesConfirmHandler}
                />
            }
            sortingColumn={{
                sortingField: props.sorting?.sortField,
            }}
            sortingDescending={props.sorting?.sortOrder === "sort.desc"}
            onSortingChange={props.onSortingChange}
            empty={<EmptyTableBox />}
            columnDefinitions={currentColumnDefinitions}
            items={orders.data?.orders || []}
            resizableColumns={true}
            variant="full-page"
            wrapLines
            stripedRows={currentPreferences.stripedRows}
            loading={isTableLoading}
            loadingText={getString("common.loading")}
        />
    );
};

export default OrderItemsTable;
